import { useCallback, useMemo, memo } from 'react';
import {
  ReactHookFormAutocomplete,
  Button,
  Grid,
  Popper,
  PopperActions,
  PopperContent,
  PopperTitle,
  Typography,
  useTranslations,
  useHeroSnackbar,
  usePubSub,
  buildFilterOptionsAsObject,
  ISecurityScoreOption,
  TSecurityScoreName,
  getSecurityScoreOptions,
  getPasswordStrengthOptions,
  TPasswordStrengthName,
  IPasswordStrengthOption,
} from '@uniqkey-frontend/shared-app';
import { useForm } from 'react-hook-form';
import { ApplicationGetInfo } from '@uniqkey-backend-organization-web/api-client';
import { IGetVaultPasswordsParams } from '../../../../../../hooks/useVaultsAPI/interfaces';
import {
  IApplicationOption, IPassword2FAStatusOption, IReusedPasswordOption, TReusedPassword,
} from '../../../../../../helpers/filters/interfaces';
import PubSubEventEnum from '../../../../../../enums/PubSubEventEnum';
import useApplicationsAPI from '../../../../../../hooks/useApplicationsAPI';
import { getApplicationOption } from '../../../../../../helpers/filters';
import { logException } from '../../../../../../services/sentryService';
import {
  getPassword2FAStatusOptions, getReusedPasswordOptions,
} from '../../../../../../helpers/filters/options';

export interface IGroupLoginsTabFilterSubmitResult {
  applicationName: IGetVaultPasswordsParams['applicationName'];
  securityScoreName: TSecurityScoreName;
  passwordStrengthName: TPasswordStrengthName;
  password2FAStatus: IGetVaultPasswordsParams['password2FAStatus'];
  reusedPassword: TReusedPassword;
}

interface IGroupLoginsTabFilterProps {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onSubmit: (result: IGroupLoginsTabFilterSubmitResult) => void;
  onClose: () => void;
  initialValues: IGroupLoginsTabFilterSubmitResult;
  groupId: string;
}

interface IFormValues {
  application: IApplicationOption | null;
  securityScore: ISecurityScoreOption | null;
  passwordStrength: IPasswordStrengthOption | null;
  password2FAStatus: IPassword2FAStatusOption | null;
  reusedPassword: IReusedPasswordOption | null;
}

const POPPER_MIN_WIDTH = { minWidth: 500 };

const GroupLoginsTabFilter = (props: IGroupLoginsTabFilterProps) => {
  const {
    isOpen, anchorEl, onSubmit, onClose, initialValues, groupId,
  } = props;
  const { t } = useTranslations();
  const { showError } = useHeroSnackbar();
  const { getServicesListForGroup } = useApplicationsAPI();

  const [securityScoreOptions, securityScoreOptionsAsObject] = useMemo(() => {
    const options = getSecurityScoreOptions(t);
    const optionsAsObject = buildFilterOptionsAsObject(options);
    return [options, optionsAsObject];
  }, [t]);

  const [password2FAStatusOptions, password2FAStatusOptionsAsObject] = useMemo(() => {
    const options = getPassword2FAStatusOptions(t);
    const optionsAsObject = buildFilterOptionsAsObject(options);
    return [options, optionsAsObject];
  }, [t]);

  const [passwordStrengthOptions, passwordStrengthOptionsAsObject] = useMemo(() => {
    const options = getPasswordStrengthOptions(t);
    const optionsAsObject = buildFilterOptionsAsObject(options);
    return [options, optionsAsObject];
  }, [t]);

  const [reusedPasswordOptions, reusedPasswordOptionsAsObject] = useMemo(() => {
    const options = getReusedPasswordOptions(t);
    const optionsAsObject = buildFilterOptionsAsObject(options);
    return [options, optionsAsObject];
  }, [t]);

  const {
    handleSubmit, control, reset, setValue, formState: { isDirty },
  } = useForm<IFormValues>({
    defaultValues: {
      application: getApplicationOption(initialValues.applicationName),
      securityScore: securityScoreOptionsAsObject[
        initialValues.securityScoreName as keyof typeof securityScoreOptionsAsObject
      ] ?? null,
      password2FAStatus: password2FAStatusOptionsAsObject[
        initialValues.password2FAStatus as keyof typeof password2FAStatusOptionsAsObject
      ] ?? null,
      passwordStrength: passwordStrengthOptionsAsObject[
        initialValues.passwordStrengthName as keyof typeof passwordStrengthOptionsAsObject
      ] ?? null,
      reusedPassword: reusedPasswordOptionsAsObject[
        initialValues.reusedPassword as keyof typeof reusedPasswordOptionsAsObject
      ] ?? null,
    },
  });

  const handleGetApplicationsRequest = useCallback(async (searchQuery: string) => {
    try {
      const { data } = await getServicesListForGroup({
        page: 1, pageLength: 100, groupId, searchQuery,
      });
      return data;
    } catch (e) {
      showError({
        text: t('common.somethingWentWrong'),
      });
      logException(e, {
        message: 'GroupLoginsTabFilter/handleGetApplicationsRequest exception',
      });
      return [];
    }
  }, [getServicesListForGroup, groupId, showError, t]);

  const handleGetApplicationsResponseParser = useCallback(
    (applicationsToParse: ApplicationGetInfo[]) => applicationsToParse.map(
      (application: ApplicationGetInfo) => getApplicationOption(
        application.name,
      ),
    ),
    [],
  );

  const handleOnSubmit = useCallback((value: IFormValues): void => {
    const {
      application, securityScore, passwordStrength, password2FAStatus, reusedPassword,
    } = value;
    onSubmit({
      applicationName: application?.value,
      securityScoreName: securityScore?.value,
      passwordStrengthName: passwordStrength?.value,
      password2FAStatus: password2FAStatus?.value,
      reusedPassword: reusedPassword?.value,
    });
    onClose();
    reset(value); // set default values to the selected ones to reset isDirty
  }, [reset, onSubmit, onClose]);

  const clearAll = useCallback(() => {
    setValue('application', null, { shouldDirty: true });
    setValue('securityScore', null, { shouldDirty: true });
    setValue('password2FAStatus', null, { shouldDirty: true });
    setValue('passwordStrength', null, { shouldDirty: true });
    setValue('reusedPassword', null, { shouldDirty: true });
  }, [setValue]);

  const handleOnFadeExited = useCallback(() => {
    reset(); // reset form values when Popper is closed and animation has finished
  }, [reset]);

  const handleReset = useCallback(() => {
    reset({
      application: null,
      securityScore: null,
      password2FAStatus: null,
      passwordStrength: null,
      reusedPassword: null,
    });
  }, [reset]);
  usePubSub(PubSubEventEnum.RESET_FILTER, handleReset);

  return (
    <Popper
      anchorEl={anchorEl}
      offsetX={-8}
      offsetY={16}
      placement="bottom-start"
      open={isOpen}
      sx={POPPER_MIN_WIDTH}
      onFadeExited={handleOnFadeExited}
    >
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <PopperTitle onClose={onClose}>
          {t('common.filterBy')}
        </PopperTitle>
        <PopperContent>
          <Grid container direction="column" rowSpacing={2}>
            <Grid item container alignItems="center">
              <Grid item xs={3}>
                <Typography>
                  {t('groupLoginsTabFilter.reusedPassword.label')}
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <ReactHookFormAutocomplete
                  name="reusedPassword"
                  options={reusedPasswordOptions}
                  placeholder={t('groupLoginsTabFilter.reusedPassword.placeholder')}
                  control={control}
                />
              </Grid>
            </Grid>
            <Grid item container alignItems="center">
              <Grid item xs={3}>
                <Typography>
                  {t('groupLoginsTabFilter.service.label')}
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <ReactHookFormAutocomplete
                  t={t}
                  name="application"
                  placeholder={t('groupLoginsTabFilter.service.placeholder')}
                  dataSourceRequest={handleGetApplicationsRequest}
                  dataSourceResponseParser={handleGetApplicationsResponseParser}
                  control={control}
                />
              </Grid>
            </Grid>
            <Grid item container alignItems="center">
              <Grid item xs={3}>
                <Typography>
                  {t('groupLoginsTabFilter.securityScore.label')}
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <ReactHookFormAutocomplete
                  name="securityScore"
                  options={securityScoreOptions}
                  placeholder={t('groupLoginsTabFilter.securityScore.placeholder')}
                  control={control}
                />
              </Grid>
            </Grid>
            <Grid item container alignItems="center">
              <Grid item xs={3}>
                <Typography>
                  {t('groupLoginsTabFilter.password2FAStatus.label')}
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <ReactHookFormAutocomplete
                  name="password2FAStatus"
                  options={password2FAStatusOptions}
                  placeholder={t('groupLoginsTabFilter.password2FAStatus.placeholder')}
                  control={control}
                />
              </Grid>
            </Grid>
            <Grid item container alignItems="center">
              <Grid item xs={3}>
                <Typography>
                  {t('groupLoginsTabFilter.passwordStrength.label')}
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <ReactHookFormAutocomplete
                  name="passwordStrength"
                  options={passwordStrengthOptions}
                  placeholder={t('groupLoginsTabFilter.passwordStrength.placeholder')}
                  control={control}
                />
              </Grid>
            </Grid>
          </Grid>
        </PopperContent>
        <PopperActions>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button disabled={!isDirty} fullWidth type="submit">{t('common.apply')}</Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={clearAll}
              >
                {t('common.clearAll')}
              </Button>
            </Grid>
          </Grid>
        </PopperActions>
      </form>
    </Popper>
  );
};

export default memo(GroupLoginsTabFilter);
